import React, {
  forwardRef,
  useCallback,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import clsx from "clsx";

import { useSyncConstraintValidationMessage } from "../../../hooks/use-sync-constraint-validation-message.ts";

import styles from "./text-field.module.css";

export type TextFieldLabelLayout = "stacked" | "inline";

export type TextFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "id" | "label"
> & {
  label?: string;
  id: string;
  required?: boolean;
  /** Optional icon inside the field on the left (e.g. search magnifier). */
  startIcon?: ReactNode;
  hint?: string;
  /** Shown under the field and mirrored into `setCustomValidity` so `reportValidity()` shows the native browser tooltip. */
  error?: string;
  /** When `false`, the error text is not shown under the field (tooltip only); a visually-hidden copy is kept for assistive tech. */
  showErrorMessage?: boolean;
  /** `stacked` = label above field (default). `inline` = label left, field right (one row). */
  labelLayout?: TextFieldLabelLayout;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      id,
      required = false,
      startIcon,
      hint,
      error,
      showErrorMessage = true,
      labelLayout = "stacked",
      className,
      disabled,
      readOnly,
      ...rest
    },
    ref
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref != null) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [ref],
    );

    useSyncConstraintValidationMessage(inputRef, error);

    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const describedBy =
      error != null && error !== ""
        ? errorId
        : hint != null && hint !== ""
          ? hintId
          : undefined;
    const hasError = error != null && error !== "";

    const inline = labelLayout === "inline";

    return (
      <div className={clsx(styles.wrapper, inline && styles.wrapperInline)}>
        {label ? (
          <label
            className={clsx(
              "sub-heading-md",
              styles.label,
              inline && styles.labelInline,
            )}
            htmlFor={id}
          >
            {label}
            {required ? (
              <span className="text-danger text-nowrap" aria-hidden>
                {"\u00A0*"}
              </span>
            ) : null}
          </label>
        ) : null}
        <div
          className={clsx(
            inline ? styles.control : styles.controlPassThrough,
          )}
        >
          {startIcon != null ? (
            <div className={styles.inputShell}>
              <span className={styles.inputStartIcon} aria-hidden>
                {startIcon}
              </span>
              <input
                ref={setInputRef}
                id={id}
                disabled={disabled}
                readOnly={readOnly}
                className={clsx(
                  styles.input,
                  styles.inputWithStartIcon,
                  "body-sm",
                  className,
                )}
                aria-invalid={hasError ? true : undefined}
                aria-describedby={describedBy}
                {...rest}
              />
            </div>
          ) : (
            <input
              ref={setInputRef}
              id={id}
              disabled={disabled}
              readOnly={readOnly}
              className={clsx(styles.input, "body-sm", className)}
              aria-invalid={hasError ? true : undefined}
              aria-describedby={describedBy}
              {...rest}
            />
          )}
          {hasError ? (
            showErrorMessage ? (
              <p id={errorId} className={clsx(styles.message, styles.messageError)} role="alert">
                {error}
              </p>
            ) : (
              <span id={errorId} className="visually-hidden" role="alert">
                {error}
              </span>
            )
          ) : hint != null && hint !== "" ? (
            <p id={hintId} className={clsx(styles.message, styles.messageHint)}>
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
);

TextField.displayName = "TextField";
